/**
 * Wholesale Preferences — manage the cleared-family list. The matcher
 * uses this list to decide which Title Statuses count as "cleared"; the
 * Adesa Cleared List filter panel toggles within it on a per-run basis,
 * but the durable list lives here.
 */
import React, { useEffect, useState } from "react"
import { Trash2 } from "lucide-react"

import Layout from "../../../components/Layout"
import DealerGroupRequiredNotice from "../../../components/DealerGroupRequiredNotice"
import { THEME } from "../../../config/theme"
import { useDealerGroup } from "../../../context/DealerGroupContext"
import { WholesaleProvider, useWholesale } from "../../../context/WholesaleContext"

export default function WholesalePreferencesPage() {
  return (
    <Layout>
      <WholesaleProvider>
        <WholesalePreferencesPageContent />
      </WholesaleProvider>
    </Layout>
  )
}

function WholesalePreferencesPageContent() {
  const {
    clearedFamilies,
    clearedFamiliesLoading,
    clearedFamiliesError,
    refreshClearedFamilies,
    addClearedFamily,
    removeClearedFamily,
  } = useWholesale()
  const { dealerGroupId } = useDealerGroup()

  const [draft, setDraft] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    refreshClearedFamilies()
  }, [refreshClearedFamilies])

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault()
    const trimmed = draft.trim()
    if (!trimmed || submitting) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await addClearedFamily(trimmed)
      setDraft("")
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to add family")
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemove = async (family: string) => {
    if (!window.confirm(`Remove "${family}" from the cleared families list?`)) return
    try {
      await removeClearedFamily(family)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to remove family")
    }
  }

  if (dealerGroupId === null) {
    return (
      <div style={{ padding: THEME.spacing.lg, fontFamily: THEME.fonts.primary, maxWidth: 600 }}>
        <h1 style={{ margin: `0 0 ${THEME.spacing.md} 0`, fontSize: 24, fontWeight: 600, color: THEME.colors.neutral.light.textPrimary }}>
          Wholesale Preferences
        </h1>
        <DealerGroupRequiredNotice surface="wholesale preferences" />
      </div>
    )
  }

  return (
    <div
      style={{
        padding: THEME.spacing.lg,
        fontFamily: THEME.fonts.primary,
        display: "flex",
        flexDirection: "column",
        gap: THEME.spacing.lg,
        maxWidth: 600,
      }}
    >
      <h1
        style={{
          margin: 0,
          fontSize: 24,
          fontWeight: 600,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        Wholesale Preferences
      </h1>

      <p
        style={{
          margin: 0,
          fontSize: 14,
          color: THEME.colors.neutral.light.textSecondary,
        }}
      >
        Title-Status family prefixes that count as <strong>cleared</strong>.
        The matcher reads this list when reconciling Adesa rows against
        Tekion. Family names are uppercased automatically.
      </p>

      <FamiliesList
        loading={clearedFamiliesLoading}
        error={clearedFamiliesError}
        families={clearedFamilies}
        onRemove={handleRemove}
      />

      <form
        onSubmit={handleAdd}
        style={{ display: "flex", gap: THEME.spacing.sm, alignItems: "stretch" }}
      >
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Add a family (e.g. PROBLEM)"
          style={{
            flex: 1,
            padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
            fontSize: 14,
            border: `1px solid ${THEME.colors.neutral.light.border}`,
            borderRadius: THEME.borderRadius.md,
            fontFamily: THEME.fonts.primary,
          }}
        />
        <button
          type="submit"
          disabled={submitting || draft.trim() === ""}
          style={{
            padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
            fontSize: 14,
            fontWeight: 500,
            color: THEME.colors.white,
            backgroundColor: THEME.colors.navy,
            border: "none",
            borderRadius: THEME.borderRadius.md,
            cursor: submitting || draft.trim() === "" ? "not-allowed" : "pointer",
            opacity: submitting || draft.trim() === "" ? 0.5 : 1,
            userSelect: "none",
            transition: "opacity 0.15s",
            display: "inline-flex",
            alignItems: "center",
            gap: THEME.spacing.xs,
            fontFamily: THEME.fonts.primary,
            whiteSpace: "nowrap",
          }}
        >
          {submitting ? "Adding…" : "Add"}
        </button>
      </form>

      {submitError && (
        <span style={{ color: THEME.colors.semantic.danger, fontSize: 13 }}>
          {submitError}
        </span>
      )}
    </div>
  )
}

interface ListProps {
  loading: boolean
  error: string | null
  families: ReturnType<typeof useWholesale>["clearedFamilies"]
  onRemove: (family: string) => void
}

function FamiliesList({ loading, error, families, onRemove }: ListProps) {
  if (loading) {
    return (
      <span style={{ color: THEME.colors.neutral.light.textSecondary }}>
        Loading…
      </span>
    )
  }
  if (error) {
    return <span style={{ color: THEME.colors.semantic.danger }}>{error}</span>
  }
  if (families.length === 0) {
    return (
      <span style={{ color: THEME.colors.neutral.light.textSecondary }}>
        No cleared families. Add at least one for the matcher to consider
        any Title Status cleared.
      </span>
    )
  }

  return (
    <ul
      style={{
        listStyle: "none",
        margin: 0,
        padding: 0,
        border: `1px solid ${THEME.colors.neutral.light.border}`,
        borderRadius: THEME.borderRadius.md,
        background: THEME.colors.neutral.light.surface,
      }}
    >
      {families.map((entry, index) => (
        <li
          key={entry.id}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
            borderTop:
              index === 0
                ? "none"
                : `1px solid ${THEME.colors.neutral.light.border}`,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: THEME.colors.neutral.light.textPrimary,
              }}
            >
              {entry.family}
            </div>
            <div
              style={{
                fontSize: 12,
                color: THEME.colors.neutral.light.textSecondary,
              }}
            >
              Added by {entry.created_by_username ?? "?"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onRemove(entry.family)}
            aria-label={`Remove ${entry.family}`}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: THEME.colors.neutral.light.textSecondary,
              padding: THEME.spacing.xs,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Trash2 size={16} />
          </button>
        </li>
      ))}
    </ul>
  )
}
